import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminDashboard from "../AdminDashboard/admindash";
import {
  PenTool,
  Image as ImageIcon,
  Tag,
  User as UserIcon,
  Eye,
  Save,
  ChevronLeft,
  Sparkles,
  CheckCircle,
  Loader2,
  FileText,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import API_URL from "../../config";


const UpdateBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Form State
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Travel');
  const [content, setContent] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [author, setAuthor] = useState('Travel & Explore Community');

  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');

  // Fetch existing blog data
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/blogs/${id}`);
        const result = await response.json();

        if (response.ok && result.blog) {
          setTitle(result.blog.title);
          setCategory(result.blog.category);
          setContent(result.blog.content);
          setCoverImageUrl(result.blog.coverImageUrl || '');
          setAuthor(result.blog.author || 'Travel & Explore Community');
        } else {
          setError(result.message || 'Blog identity not found');
          toast.error("Story retrieval failed.");
        }
      } catch (err) {
        console.error(err);
        setError('Failed to fetch blog data');
        toast.error("Server connection error during synchronization.");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchBlog();
  }, [id]);

  const handleUpdateBlog = async (e) => {
    e.preventDefault();
    setIsUpdating(true);

    const blogData = { title, category, content, coverImageUrl, author };

    try {
      const response = await fetch(`${API_URL}/blogs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blogData)
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Narrative refined successfully.');
        // Brief delay before navigation for toast visibility
        setTimeout(() => navigate('/view'), 1500);
      } else {
        toast.error(result.message || 'Unable to update story.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Terminal error during update.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) return (
    <AdminDashboard>
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <Loader2 className="animate-spin text-sky-600" size={48} />
        <p className="text-[10px] font-black uppercase tracking-widest text-sky-900/40">Synchronizing Story Assets...</p>
      </div>
    </AdminDashboard>
  );

  if (error) return (
    <AdminDashboard>
      <div className="flex flex-col items-center justify-center py-20 bg-rose-50 rounded-[3rem] border border-rose-100 gap-6 max-w-2xl mx-auto mt-20">
        <div className="w-20 h-20 bg-white rounded-full shadow-inner flex items-center justify-center text-rose-500">
          <AlertCircle size={40} />
        </div>
        <div className="text-center">
          <h3 className="text-xl font-black text-rose-950 italic">Synchronization Failure</h3>
          <p className="text-xs text-rose-400 font-bold mt-2 px-10">{error}</p>
          <Link to="/view" className="mt-8 px-8 py-3.5 bg-rose-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-rose-500/20 active:scale-95 inline-block">Return to Registry</Link>
        </div>
      </div>
    </AdminDashboard>
  );

  return (
    <AdminDashboard>
      <div className="max-w-7xl mx-auto font-sans">

        {/* Header Navigation */}
        <div className="flex justify-between items-end mb-12 border-b border-gray-100 pb-10">
          <div>
            <Link to="/view" className="inline-flex items-center gap-2 text-sky-600 font-black text-[10px] uppercase tracking-widest mb-4 hover:-translate-x-1 transition-transform">
              <ChevronLeft size={14} /> Back to Registry
            </Link>
            <h1 className="text-3xl font-black text-sky-950 tracking-tight flex items-center gap-4 leading-none">
              <PenTool className="text-sky-500" size={36} />
              Refine <span className="text-sky-600 italic">Narrative Flow</span>
            </h1>
            <p className="text-gray-400 mt-3 font-bold text-sm max-w-lg">Modify and enhance your existing stories for premium audience engagement.</p>
          </div>
          <div className="hidden lg:flex px-6 py-4 bg-sky-50 rounded-2xl border border-sky-100 items-center gap-4">
            <Sparkles className="text-sky-400" size={24} />
            <div>
              <p className="text-[9px] font-black text-sky-900/40 uppercase tracking-widest">Story ID</p>
              <h4 className="text-xs font-black text-sky-900 tracking-tighter">{id.toUpperCase()}</h4>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Editor Column */}
          <div className="lg:col-span-7 space-y-8">
            <div className="bg-white rounded-[3rem] shadow-xl border border-gray-100 overflow-hidden ring-1 ring-gray-50">
              <div className="bg-gray-50/50 px-10 py-6 border-b border-gray-100 flex items-center justify-between">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <FileText size={14} className="text-sky-300" /> Editorial Desk
                </span>
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-rose-200"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-200"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-200 shadow-inner"></div>
                </div>
              </div>

              <form onSubmit={handleUpdateBlog} className="p-10 space-y-8">
                {/* Title */}
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-sky-950 uppercase tracking-widest flex items-center gap-2">
                    <Tag size={12} className="text-sky-300" /> Story Identity
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter a captivating title..."
                    required
                    className="w-full px-6 py-5 bg-gray-50 border-2 border-transparent rounded-[1.5rem] focus:ring-4 focus:ring-sky-500/10 focus:border-sky-500 focus:bg-white transition-all text-xl font-black text-sky-950 placeholder:text-gray-200"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Category */}
                  <div className="space-y-3">
                    <label className="text-[11px] font-black text-sky-950 uppercase tracking-widest">Target Context</label>
                    <div className="relative">
                      <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-sky-500 transition-all font-black text-xs uppercase tracking-widest text-sky-900 appearance-none cursor-pointer"
                      >
                        <option value="Travel">Travel</option>
                        <option value="Adventure">Adventure</option>
                        <option value="Events">Events</option>
                        <option value="Food">Food</option>
                      </select>
                      <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-sky-300">
                        <Tag size={16} />
                      </div>
                    </div>
                  </div>

                  {/* Author */}
                  <div className="space-y-3">
                    <label className="text-[11px] font-black text-sky-950 uppercase tracking-widest flex items-center gap-2">
                      <UserIcon size={12} className="text-sky-300" /> Lead Author
                    </label>
                    <input
                      type="text"
                      value={author}
                      onChange={(e) => setAuthor(e.target.value)}
                      className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-sky-500 transition-all font-bold text-sky-950"
                    />
                  </div>
                </div>

                {/* Cover Image URL */}
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-sky-950 uppercase tracking-widest flex items-center gap-2">
                    <ImageIcon size={12} className="text-sky-300" /> Narrative Visual URL
                  </label>
                  <div className="relative group">
                    <input
                      type="text"
                      value={coverImageUrl}
                      onChange={(e) => setCoverImageUrl(e.target.value)}
                      placeholder="https://images.unsplash.com/..."
                      className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-sky-500 transition-all pr-14 font-medium text-sky-900"
                    />
                    <div className="absolute right-6 top-1/2 -translate-y-1/2">
                      {coverImageUrl ? (
                        <CheckCircle size={18} className="text-emerald-500 animate-pulse" />
                      ) : (
                        <ImageIcon size={18} className="text-gray-300" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-3">
                  <label className="text-[11px] font-black text-sky-950 uppercase tracking-widest">Narrative Payload</label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Once upon a time..."
                    required
                    rows="12"
                    className="w-full px-8 py-6 bg-gray-50 border-2 border-transparent rounded-[2rem] focus:ring-4 focus:ring-sky-500/10 focus:border-sky-500 focus:bg-white transition-all resize-none font-serif leading-loose text-lg text-sky-950 shadow-inner"
                  ></textarea>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className={`
                        flex-grow py-5 rounded-[1.5rem] font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl flex items-center justify-center gap-4 transition-all transform active:scale-95
                        ${isUpdating ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-sky-950 text-white hover:bg-sky-800 shadow-sky-950/20'}
                      `}
                  >
                    {isUpdating ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <>
                        <Save size={18} />
                        Publish Synchronizations
                      </>
                    )}
                  </button>
                  <Link
                    to="/view"
                    className="px-10 py-5 bg-white border-2 border-gray-100 text-sky-950 rounded-[1.5rem] font-black text-[10px] uppercase tracking-widest hover:bg-gray-50 transition-all flex items-center justify-center shadow-lg"
                  >
                    Discard
                  </Link>
                </div>
              </form>
            </div>
          </div>

          {/* Live Preview Column */}
          <div className="lg:col-span-5 space-y-6">
            <div className="sticky top-10">
              <div className="flex items-center justify-between mb-6 px-4">
                <h3 className="text-[10px] font-black text-sky-950 uppercase tracking-[0.2em] flex items-center gap-3">
                  <Eye size={16} className="text-sky-500" /> Elite Reader Preview
                </h3>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">Real-time Stream</span>
                </div>
              </div>

              <div className="bg-white rounded-[3.5rem] shadow-2xl border border-gray-100 overflow-hidden ring-12 ring-gray-50 transition-all duration-700 hover:ring-sky-50 group/preview">
                {/* Visual Header */}
                <div className="h-64 bg-gray-100 relative overflow-hidden">
                  {coverImageUrl ? (
                    <img
                      src={coverImageUrl}
                      alt="Story Preview"
                      className="w-full h-full object-cover transition duration-[2s] group-hover/preview:scale-110"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1000&auto=format&fit=crop";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-sky-900/10">
                      <ImageIcon size={64} className="mb-2" strokeWidth={1} />
                      <span className="text-[10px] font-black uppercase tracking-[0.3em]">Visual Hub</span>
                    </div>
                  )}
                  <div className="absolute top-8 left-8">
                    <span className="px-4 py-1.5 bg-white/95 backdrop-blur-md text-sky-600 rounded-full text-[9px] font-black uppercase tracking-widest shadow-xl border border-white/50">
                      {category}
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-40"></div>
                </div>

                {/* Reading Area */}
                <div className="p-12 space-y-6">
                  <h2 className={`text-3xl font-black text-sky-950 leading-[1.1] tracking-tight group-hover/preview:text-sky-600 transition-colors ${!title && 'opacity-10'}`}>
                    {title || 'Narrative Title Pending...'}
                  </h2>

                  <div className="flex items-center gap-4 py-5 border-y border-gray-50">
                    <div className="w-10 h-10 rounded-2xl bg-sky-950 flex items-center justify-center text-white text-xs font-black shadow-lg shadow-sky-950/20">
                      {author.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-[11px] font-black text-sky-950 uppercase tracking-tight leading-none mb-1">{author}</p>
                      <p className="text-[10px] text-gray-400 font-bold tracking-tight uppercase">Elite Contributor • Editorial Preview</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <p className={`text-gray-400 font-serif leading-[2] text-lg line-clamp-[8] italic ${!content && 'opacity-10'}`}>
                      "{content || 'Awaiting thematic payload for editorial review. Every word written here will synchronize across the global feed instantly...'}"
                    </p>
                  </div>

                  <div className="pt-8 flex gap-3">
                    <div className="w-12 h-1.5 bg-sky-600 rounded-full transition-all group-hover/preview:w-20"></div>
                    <div className="w-3 h-1.5 bg-gray-100 rounded-full"></div>
                    <div className="w-3 h-1.5 bg-gray-100 rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Security Badge */}
              <div className="mt-8 p-8 bg-gradient-to-br from-sky-950 to-sky-900 rounded-[2.5rem] text-white relative overflow-hidden group/badge shadow-2xl shadow-sky-950/20">
                <div className="relative z-10">
                  <h4 className="font-black text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                    <CheckCircle className="text-emerald-400" size={16} /> Encryption Active
                  </h4>
                  <p className="text-[10px] text-sky-200/50 mt-3 leading-relaxed font-bold italic uppercase tracking-tighter">
                    Terminal refinements are synchronized via secure tunnel. Changes are terminal and irreversible upon publication.
                  </p>
                </div>
                <PenTool className="absolute -right-6 -bottom-6 text-white/5 w-32 h-32 group-hover/badge:rotate-12 transition duration-700" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminDashboard>
  );
};

export default UpdateBlog;
